import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, map, Observable, throwError } from 'rxjs';
import { environment } from '../../environment/environment';
import {
  NewsBannerBackendModel,
  NewsBannerCreateModel,
  NewsBannerModel,
  NewsBannerOrderModel,
  NewsBannerSlideModel,
  NewsBannerUpdateModel,
} from '../models/news-banner.model';
import { base64ToFile } from '../utils/file.utils';
import { TrainingCategoryService } from './training-category.service';

@Injectable({
  providedIn: 'root',
})
export class NewsBannerService {
  constructor(
    private http: HttpClient,
    private courseTypeService: TrainingCategoryService
  ) {}

  getAllNewsBanner(): Observable<NewsBannerModel[]> {
    const formData = new FormData();
    formData.append('mode', 'getAllBanners');

    return this.http
      .post<{
        success: boolean;
        message: string;
        data: NewsBannerBackendModel[];
      }>(environment.authority_short + 'banner.php', formData)
      .pipe(
        map(response =>
          response.data?.map(banner => this.parseBackendNewsBannerModel(banner))
        ),
        catchError(() => {
          return throwError(() => new Error('Failed to fetch news banners'));
        })
      );
  }

  getNewsBannerById(id: number): Observable<NewsBannerModel> {
    const formData = new FormData();
    formData.append('mode', 'getBanner');
    formData.append('id', id.toString());

    return this.http
      .post<{
        success: boolean;
        message: string;
        data: NewsBannerBackendModel;
      }>(environment.authority_short + 'banner.php', formData)
      .pipe(
        map(response => this.parseBackendNewsBannerModel(response.data)),
        catchError(() => {
          return throwError(() => new Error('Failed to fetch news banner'));
        })
      );
  }

  createNewsBanner(
    newsBannerCreateModel: NewsBannerCreateModel
  ): Observable<void> {
    const formData = new FormData();

    formData.append('mode', 'createBanner');

    newsBannerCreateModel.title &&
      formData.append('title', newsBannerCreateModel.title);
    newsBannerCreateModel.subtitle &&
      formData.append('sub_title', newsBannerCreateModel.subtitle);
    newsBannerCreateModel.description &&
      formData.append('description', newsBannerCreateModel.description);
    newsBannerCreateModel.teaserText &&
      formData.append('teaser_text', newsBannerCreateModel.teaserText);
    newsBannerCreateModel.contentType &&
      formData.append('content_type', newsBannerCreateModel.contentType);
    newsBannerCreateModel.image &&
      formData.append('image', newsBannerCreateModel.image);
    newsBannerCreateModel.categoryId &&
      formData.append(
        'id_category',
        newsBannerCreateModel.categoryId.toString()
      );
    newsBannerCreateModel.link &&
      formData.append('link', newsBannerCreateModel.link);
    newsBannerCreateModel.linkText &&
      formData.append('link_text', newsBannerCreateModel.linkText);

    return this.http
      .post<{
        success: boolean;
        message: string;
      }>(environment.authority_short + 'banner.php', formData)
      .pipe(
        map(() => undefined),
        catchError(error => {
          return throwError(() => error);
        })
      );
  }

  updateNewsBanner(
    newsBannerUpdateModel: NewsBannerUpdateModel
  ): Observable<void> {
    const formData = new FormData();

    formData.append('mode', 'updateBanner');

    newsBannerUpdateModel.id &&
      formData.append('id', newsBannerUpdateModel.id.toString());
    newsBannerUpdateModel.title &&
      formData.append('title', newsBannerUpdateModel.title);
    newsBannerUpdateModel.subtitle &&
      formData.append('sub_title', newsBannerUpdateModel.subtitle);
    newsBannerUpdateModel.description &&
      formData.append('description', newsBannerUpdateModel.description);
    newsBannerUpdateModel.teaserText &&
      formData.append('teaser_text', newsBannerUpdateModel.teaserText);
    newsBannerUpdateModel.contentType &&
      formData.append('content_type', newsBannerUpdateModel.contentType);
    newsBannerUpdateModel.image &&
      formData.append('image', newsBannerUpdateModel.image);
    newsBannerUpdateModel.categoryId &&
      formData.append(
        'id_category',
        newsBannerUpdateModel.categoryId.toString()
      );
    newsBannerUpdateModel.link &&
      formData.append('link', newsBannerUpdateModel.link);
    newsBannerUpdateModel.linkText &&
      formData.append('link_text', newsBannerUpdateModel.linkText);

    return this.http
      .post<{
        success: boolean;
        message: string;
      }>(environment.authority_short + 'banner.php', formData)
      .pipe(
        map(() => undefined),
        catchError(error => {
          return throwError(() => error);
        })
      );
  }

  deleteNewsBanner(id: number): Observable<void> {
    const formData = new FormData();
    formData.append('mode', 'deleteBanner');
    formData.append('id', id.toString());

    return this.http
      .post<{
        success: boolean;
        message: string;
      }>(environment.authority_short + 'banner.php', formData)
      .pipe(
        map(() => undefined),
        catchError(() => {
          return throwError(() => new Error('Banner deletion failed'));
        })
      );
  }

  updateNewsBannerOrder(
    newsBannerOrder: NewsBannerOrderModel[]
  ): Observable<void> {
    const formData = new FormData();
    formData.append('mode', 'reorderBanners');

    newsBannerOrder.forEach((order, index) => {
      formData.append(`id[${index}]`, order.id.toString());
      formData.append(`position[${index}]`, order.position.toString());
    });

    return this.http
      .post<{
        success: boolean;
        message: string;
      }>(environment.authority_short + 'banner.php', formData)
      .pipe(
        map(() => undefined),
        catchError(() => {
          return throwError(() => new Error('Failed to reorder news banners'));
        })
      );
  }

  private parseBackendNewsBannerModel(
    newsBanner: NewsBannerBackendModel
  ): NewsBannerModel {
    const newsBannerModel: NewsBannerModel = {
      id: newsBanner.id,
      title: newsBanner.title,
      subtitle: newsBanner.sub_title,
      description: newsBanner.description,
      teaserText: newsBanner.teaser_text,
      contentType: newsBanner.content_type,
      image: newsBanner.image
        ? base64ToFile(newsBanner.image, newsBanner.title)
        : null,
      position: newsBanner.position,
      category: newsBanner.category
        ? this.courseTypeService.parseBackendModel(newsBanner.category)
        : null,
      link: newsBanner.link,
      linkText: newsBanner.link_text,
    };

    return newsBannerModel;
  }

  public mapNewsBannersToSlides(
    newsBanners: NewsBannerModel[]
  ): NewsBannerSlideModel[] {
    if (newsBanners.length === 0) return [];

    return newsBanners.map(
      (newsBanner: NewsBannerModel): NewsBannerSlideModel => {
        return {
          newsBannerId: newsBanner.id,
          imageUrl: newsBanner.image
            ? URL.createObjectURL(newsBanner.image)
            : null,
          title: newsBanner.title,
          subtitle: newsBanner.subtitle,
          description: newsBanner.description,
          teaserText: newsBanner.teaserText,
          color50: newsBanner.category?.color50,
          color0: newsBanner.category?.color0 ?? '#1C665B',
          color70: newsBanner.category?.color70 ?? '#bbd1ce',
          color80: newsBanner.category?.color80 ?? '#D2E0DE',
          color90: newsBanner.category?.color90,
          link: newsBanner.link,
          linkText: newsBanner.linkText,
        };
      }
    );
  }
}
